import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { ReactNode } from "react";
import { createContext, useMemo, useState } from "react";
import { getExpectedRole } from "./LogtoApolloContext";

export type Permission =
  | "read:contact_board"
  | "reject:applicant"
  | "read:internal_insights"
  | "read:external_insights"
  | "edit:campaign"
  | "write:campaign"
  | "edit:organization"
  | "write:organization"
  | "write:location"
  | "delete:location"
  | "write:transaction"
  | "pause:campaign"
  | "stop:campaign"
  | "write:user"
  | "read:user"
  | "update:user"
  | "delete:user"
  | "read:campaign_budget"
  | "write:budget_increase"
  | "read:creative"
  | "write:creative"
  | "create:quotation"
  | "approve:ad"
  | "write:destinations"
  | "write:email_text"
  | "delete:applicant";

export interface User {
  organization: string;
  id: string;
  roles: UserRole[];
  scopes: Permission[];
  email: string;
  name: string;
  username: string;
  picture: string | null;
  region_slugs: string[] | null;
  location_slugs: string[] | null;
  can_only_see_additional_campaigns: boolean | null;
  additional_campaign_ids: string[] | null;
}

export type UpdateUserDto = {
  id: string;
  name?: string;
  organization: string;
  location_slugs: string[];
  region_slugs: string[];
  email?: string;
  picture?: string;
  can_only_see_additional_campaigns: boolean;
  additional_campaign_ids: string[];
};

export type CreateNewUserDto = {
  email: string;
  name: string;
  organization: string;
  password: string;
  username: string;
  location_slugs: string[];
  region_slugs: string[];
  can_only_see_additional_campaigns: boolean;
};

export type UserRole = "everbay_admin" | "employee" | "partner" | "customer";

export function hasAnyOfRoles(
  expectedRoles: UserRole[],
  actualRole: UserRole | null,
) {
  return expectedRoles.some((role) => role === actualRole);
}

const RolePermissionManager = {
  canSeeImpersonation: (role: UserRole | null) =>
    role != null && ["everbay_admin", "employee"].includes(role),

  canSeeMarginalReturn: (role: UserRole | null) =>
    role != null && ["everbay_admin", "employee"].includes(role),

  isEverbayEmployee: (role: UserRole | null) =>
    role != null && ["everbay_admin", "employee"].includes(role),

  hasAdvancedAccess: (role: UserRole | null) =>
    role != null && ["everbay_admin", "employee"].includes(role),

  canSeeInternalInsights: (role: UserRole | null) =>
    role != null && ["everbay_admin", "employee"].includes(role),
};

const UserNameParser = {
  parse: (fullName: string | undefined) => {
    if (!fullName) return { firstName: null, lastName: null };
    const [firstName, ...lastNameParts] = fullName.split(" ");
    return {
      firstName: firstName || null,
      lastName: lastNameParts.join(" ") || null,
    };
  },
};

function createDefaultContextValue(setUser: (user: User | null) => void) {
  return {
    user: null,
    role: null as UserRole | null,
    setUser,
    firstName: null,
    lastName: null,
    permissions: [] as Permission[],
    hasPermission: () => false,
    isAdmin: false,
    hasAdvancedAccessToCampaigns: false,
    isNotCustomer: false,
    canSeeInternalInsights: false,
    canSeeSensibleData: false,
    canSeeImpersonation: false,
    canSeeMarginalReturn: false,
    isEverbayPartnerOrganization: false,
    isEverbayEmployee: false,
  };
}

interface UserContextType {
  user: User | null;
  role: UserRole | null;
  setUser: (user: User | null) => void;
  firstName: string | null;
  lastName: string | null;
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
  isAdmin: boolean;
  hasAdvancedAccessToCampaigns: boolean;
  isNotCustomer: boolean;
  canSeeInternalInsights: boolean;
  canSeeSensibleData: boolean;
  canSeeImpersonation: boolean;
  canSeeMarginalReturn: boolean;
  isEverbayPartnerOrganization: boolean;
  isEverbayEmployee: boolean;
}

export const UserContext = createContext<UserContextType>(
  createDefaultContextValue(() => {}),
);

export function UserProvider({ children }: { readonly children: ReactNode }) {
  const { organization } = useImpersonation();
  const [user, setUser] = useState<User | null>(null);

  const contextValue = useMemo(() => {
    if (!user) {
      return createDefaultContextValue(setUser);
    }

    const roles: UserRole[] = organization != null ? ["customer"] : user.roles;
    const expectedRole = getExpectedRole(roles);
    const { firstName, lastName } = UserNameParser.parse(user.name);
    const permissions = user.scopes;

    const canSeeInternalInsights =
      RolePermissionManager.canSeeInternalInsights(expectedRole);
    const isImpersonatingOrganization = organization !== null;

    const isAdmin = expectedRole === "everbay_admin";

    return {
      user: {
        ...user,
        organization: organization ?? user.organization,
        roles,
      },
      role: expectedRole,
      setUser,
      firstName,
      lastName,
      permissions,
      hasPermission: (permission: Permission) =>
        permissions.includes(permission),
      isAdmin,
      hasAdvancedAccessToCampaigns:
        RolePermissionManager.hasAdvancedAccess(expectedRole),
      isNotCustomer: !["customer", "partner"].includes(expectedRole ?? ""),
      canSeeInternalInsights,
      canSeeSensibleData:
        canSeeInternalInsights && !isImpersonatingOrganization,
      canSeeImpersonation:
        RolePermissionManager.canSeeImpersonation(expectedRole),
      canSeeMarginalReturn:
        RolePermissionManager.canSeeMarginalReturn(expectedRole),
      isEverbayPartnerOrganization: roles.includes("partner"),
      isEverbayEmployee: RolePermissionManager.isEverbayEmployee(expectedRole),
    };
  }, [user, organization]);

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}
